import { Box, Button, Container, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCustomThemeContext } from "../../providers/customThemeProvider";
import { CoPresent, Edit, Login, ViewList } from "@mui/icons-material";
import { ReEvent } from "../../interfaces/types";
import { getEventDateOrPeriod } from "../../services/eventService";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { usePermissionContext } from "../../providers/PermissionProvider";
import { useEventContext } from "../../providers/EventProvider";

const EventDetailPage: React.FC = () => {
  const { eventId } = useParams();
  const { getEventById } = useEventContext();
  const navigate = useNavigate();
  const {
    hasSessionOverviewViewPermission,
    hasEventEditPermission,
    hasSessionSubmitPermission,
  } = usePermissionContext();
  const { updateTheme } = useCustomThemeContext();
  const [event, setEvent] = useState<ReEvent>();

  useEffect(() => {
    if (eventId) {
      const fetchedEvent = getEventById(eventId);
      setEvent(fetchedEvent);
    }
  }, [eventId, getEventById]);

  // Update the theme when the component mounts or when the event changes
  useEffect(() => {
    if (event) {
      updateTheme(event);
    } else {
      updateTheme();
    }
  }, [event]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          width: "fit-content",
          position: "absolute",
          right: 5,
          top: 145,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {event?.id && hasSessionOverviewViewPermission(event.id) && (
          <Button
            variant="contained"
            color="primary"
            sx={{ mb: 1 }} // Add margin-bottom to create space between buttons
            onClick={() => navigate("/sessionlist/" + event?.id)}
          >
            <ViewList sx={{ mr: 1 }} /> View Session list
          </Button>
        )}
        {event?.id && hasEventEditPermission(event.id) && (
          <Button
            variant="contained"
            color="primary"
            onClick={() =>
              navigate("/event/edit/" + event?.id, { state: { event: event } })
            }
          >
            <Edit sx={{ mr: 1 }} /> Edit event
          </Button>
        )}
      </Box>
      <Container
        disableGutters
        sx={{
          minHeight: "100vh",
          width: "70vw",
          position: "relative",
          marginBottom: 5,
        }}
      >
        <Box
          display="flex"
          flex="100%"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <Box
            flex={1}
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Typography variant="h2" sx={{ fontWeight: "bold" }}>
              {event?.name}
            </Typography>
            <Typography variant="h3">
              {event ? getEventDateOrPeriod(event) : ""}
            </Typography>
          </Box>
          <Box
            flex={1}
            display="flex"
            flexDirection="column"
            alignItems="center"
            mt={5}
          >
            <Typography variant="h2" align={"center"} sx={{ mx: "auto" }}>
              {event?.eventConfiguration?.dashboardTitle}
            </Typography>
            <Typography variant="h5" align={"center"}>
              {event?.eventConfiguration?.dashboardWelcomeText}
            </Typography>
          </Box>
        </Box>

        <Container
          disableGutters
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            mt: 5,
          }}
        >
          <Box
            src={event?.eventConfiguration?.dashboardPictureUrl}
            component="img"
            sx={{
              width: "50%",
              maxHeight: "200px",
              objectFit: "contain",
            }}
          />
          <Box
            flex={"50%"}
            display="flex"
            flexDirection="column"
            justifyContent=""
            alignItems="flex-center"
            pl={5}
          >
            <Typography variant="h6">
              Calling all {event?.eventConfiguration?.targetAudience}!
            </Typography>
            <Typography variant="body1">{event?.description}</Typography>
          </Box>
        </Container>

        {hasSessionSubmitPermission() && (
          <Container
            disableGutters
            sx={{
              display: "flex",
              flex: 1,
              flexDirection: "row",
              alignItems: "center",
              mt: 5,
            }}
          >
            <Box
              flex={1}
              display="flex"
              flexDirection="column"
              alignContent="center"
            >
              <Typography variant="h6">
                Would you like to host a session at this event?
              </Typography>
              <Typography variant="body1">
                If you have an idea for a session that you would like to
                present, please click the button below to submit your proposal.
                We are always looking for engaging and informative sessions to
                add to our event lineup.
              </Typography>
              {/*add checks for permissions if neccessary*/}
              <Button
                variant="contained"
                color="primary"
                sx={{ width: "fit-content", mt: 2 }}
                onClick={() => {
                  navigate("/session/create/" + event?.id);
                }}
              >
                <CoPresent sx={{ mr: 1 }} />
                Submit a session
              </Button>
            </Box>
            <Box
              flex={1}
              src={event?.eventConfiguration?.logoUrl}
              component="img"
              sx={{
                maxWidth: "50%",
                maxHeight: "200px",
                objectFit: "contain",
              }}
            />
          </Container>
        )}
        {!hasSessionSubmitPermission() && (
          <Container
            disableGutters
            sx={{
              display: "flex",
              flex: 1,
              flexDirection: "row",
              alignItems: "center",
              mt: 5,
            }}
          >
            <Box
              flex={1}
              display="flex"
              flexDirection="column"
              alignContent="center"
            >
              <Typography variant="h6">
                Would you like to host a session at this event?
              </Typography>
              <Typography variant="body1">
                If you have an idea for a session that you would like to
                present, please click the button below to login.
              </Typography>
              {/*add checks for permissions if neccessary*/}
              <Button
                variant="contained"
                color="primary"
                sx={{ width: "fit-content", mt: 2 }}
                onClick={() => navigate("/login")}
              >
                <Login sx={{ mr: 1 }} />
                Login
              </Button>
            </Box>
            <Box
              flex={1}
              src={event?.eventConfiguration?.logoUrl}
              component="img"
              sx={{
                maxWidth: "50%",
                maxHeight: "200px",
                objectFit: "contain",
              }}
            />
          </Container>
        )}
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          alignContent="center"
        >
          <Typography variant="h6" textAlign={"center"} mt={5}>
            Want to find out more about the event organisers, or general
            information? Click the button to view the event website!
          </Typography>
          <Button
            variant="contained"
            color="primary"
            sx={{ width: "auto", mt: 2 }}
            fullWidth
            onClick={() =>
              window.open(event?.eventConfiguration?.websiteUrl, "_target")
            }
          >
            Visit Event Website
          </Button>
        </Box>
      </Container>
    </LocalizationProvider>
  );
};

export default EventDetailPage;
